import { isProxy, isRef, toRaw, unref } from 'vue'

type PlainValue =
  | string
  | number
  | boolean
  | null
  | PlainValue[]
  | { [key: string]: PlainValue }

interface PlainDataOptions {
  excludeKeys?: string[]
}

const DEFAULT_EXCLUDED_KEYS = new Set([
  'id',
  'createdAt',
  'updatedAt',
  'publishedAt',
  'publishedBy',
  'updatedBy',
  'archivedAt',
  'archivedBy',
])

function unwrap(value: unknown): unknown {
  const unwrapped = isRef(value) ? unref(value) : value
  return isProxy(unwrapped) ? toRaw(unwrapped) : unwrapped
}

function isFirestoreTimestamp(value: unknown): value is { toDate: () => Date } {
  return Boolean(value && typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function')
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function toPlainValue(value: unknown): PlainValue | undefined {
  const raw = unwrap(value)
  if (raw == null) return null
  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') return raw
  if (typeof raw === 'bigint') return raw.toString()
  if (typeof raw === 'function' || typeof raw === 'symbol' || typeof raw === 'undefined') return undefined
  if (raw instanceof Date) return raw.toISOString()
  if (typeof File !== 'undefined' && raw instanceof File) return undefined
  if (typeof Blob !== 'undefined' && raw instanceof Blob) return undefined
  if (isFirestoreTimestamp(raw)) return raw.toDate().toISOString()
  if (Array.isArray(raw)) return raw.map(toPlainValue).filter((item) => item !== undefined) as PlainValue[]
  if (!isPlainObject(raw)) return undefined

  const output: Record<string, PlainValue> = {}
  for (const [key, child] of Object.entries(raw)) {
    const plain = toPlainValue(child)
    if (plain !== undefined) output[key] = plain
  }
  return output
}

export function toPlainFirestoreData<T extends Record<string, unknown>>(
  value: T,
  options: PlainDataOptions = {},
): Record<string, PlainValue> {
  const excluded = new Set([...DEFAULT_EXCLUDED_KEYS, ...(options.excludeKeys || [])])
  const raw = unwrap(value)
  const source = isPlainObject(raw) ? raw : {}
  const output: Record<string, PlainValue> = {}

  for (const [key, child] of Object.entries(source)) {
    if (excluded.has(key)) continue
    const plain = toPlainValue(child)
    if (plain !== undefined) output[key] = plain
  }

  return output
}

export function cloneForEditableForm<T extends Record<string, unknown>>(value: T): Record<string, PlainValue> {
  return toPlainFirestoreData(value, { excludeKeys: [] })
}
